import React, { createContext, useContext, useState, ReactNode } from 'react';
import { User, LoginDto } from '../types';
import { authAPI } from '../services/api';

interface AuthState {
  user: User | null;
  token: string | null;
  loading: boolean;
  error: string | null;
  isAuthenticated: boolean;
}

interface AuthContextType extends AuthState {
  login: (credentials: LoginDto) => Promise<void>;
  logout: () => void;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, setState] = useState<AuthState>(() => {
    const token = localStorage.getItem('authToken');
    const userData = localStorage.getItem('user');
    let user = null;
    
    if (token && userData) {
      try {
        user = JSON.parse(userData);
      } catch (error) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
      }
    }
    
    return {
      user,
      token,
      loading: false,
      error: null,
      isAuthenticated: !!token && !!user,
    };
  });

  const login = async (credentials: LoginDto) => {
    setState(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      // Real API call to backend
      const response = await authAPI.login(credentials);
      
      if (response.data && response.data.token && response.data.user) {
        const { token, user } = response.data;
        
        localStorage.setItem('authToken', token);
        localStorage.setItem('user', JSON.stringify(user));
        
        setState({
          user,
          token,
          loading: false,
          error: null,
          isAuthenticated: true,
        });
      } else {
        throw new Error('Geçersiz giriş bilgileri');
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Giriş başarısız oldu';
      setState(prev => ({
        ...prev,
        loading: false,
        error: errorMessage,
      }));
      throw error;
    }
  };

  const logout = () => {
    authAPI.logout();
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    setState({
      user: null,
      token: null,
      loading: false,
      error: null,
      isAuthenticated: false,
    });
  };

  const clearError = () => {
    setState(prev => ({ ...prev, error: null }));
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        login,
        logout,
        clearError,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};